// Single source of truth for the App Store screenshot slides, shared by the
// Screenshot dev panel and the screenshot deep-link handler.
// `childMode: true` means the route lives under /child-mode and requires the
// app to enter child view mode (a child selected) before navigating.
export interface ScreenshotSlide {
  /** Stable key used in output filenames, e.g. "tasks". */
  key: string;
  /** Short label shown on the dev panel buttons, e.g. "1 · Tasks". */
  label: string;
  /** Route shown for this slide (the screen behind any action overlay). */
  route: string;
  /** Whether this is a /child-mode/* screen needing child view mode. */
  childMode: boolean;
  /**
   * Optional special behaviour triggered after navigating to `route`:
   * - 'firstChildTasks': open the first child's Tasks page (?childId=) and
   *   ensure >= 3 tasks are ticked today.
   * - 'rewardsPath': normalize the first child's reward path right before the
   *   capture (points back to 120, stray collections >= 150 pts voided) — task
   *   completions made by the OTHER slides shift the child's points via the
   *   daily_completions sync trigger, which can cross a station threshold and
   *   auto-collect it, breaking the curated 3 earned / 2 pending / focal-at-150
   *   composition.
   * - 'habitsList': open the first child's Habitudes page (?childId=) from the
   *   TOP, no scrolling, and wait for the anchoring list to mount before
   *   capturing. Its history is seeded OUT OF BAND by
   *   scripts/screenshots-fixtures.ts: a daily habit needs 14 occurrences over
   *   21 days to leave "Nouvelle", far more than a capture-time seed can write
   *   inside the ready timeout.
   * - 'avatarLobby': open the AvatarAlbum (collection) over the first
   *   child's Tasks page (coin-tap equivalent).
   * - 'allowanceTab': select the "Argent de poche" (allowance) segment on the
   *   rewards page so the capture shows the balance/ledger panel, not the path.
   * - 'chest' / 'reward': fire the chest (resp. reward) celebration over the
   *   route. Only 'chest' is used by the plan below; 'reward' stays wired so
   *   the celebration can be swapped back by editing this file alone.
   */
  action?: 'firstChildTasks' | 'rewardsPath' | 'habitsList' | 'avatarLobby' | 'allowanceTab' | 'chest' | 'reward';
  /**
   * Extra wait (ms) before the orchestrator starts looking for a stable frame.
   * Use for slides that take a while to settle — e.g. the celebration modal
   * animates in over a (re)launching app and only stabilizes after a few
   * seconds, so capturing too early catches it mid-transition.
   */
  settleMs?: number;
}

export const SCREENSHOT_SLIDES: ScreenshotSlide[] = [
  { key: 'tasks',     label: '1 · Tasks',     route: '/tabs/tasks',                 childMode: false, action: 'firstChildTasks', settleMs: 8000 },
  { key: 'rewards',   label: '2 · Rewards',   route: '/tabs/rewards',               childMode: false, action: 'rewardsPath',     settleMs: 9000 },
  { key: 'habits',    label: '3 · Habits',    route: '/tabs/stats',                 childMode: false, action: 'habitsList',      settleMs: 3000 },
  { key: 'lobby',     label: '4 · Lobby',     route: '/tabs/tasks',                 childMode: false, action: 'avatarLobby',     settleMs: 5000 },
  { key: 'allowance', label: '5 · Allowance', route: '/tabs/rewards?tab=allowance', childMode: false, action: 'allowanceTab',    settleMs: 1500 },
  { key: 'chest',     label: '6 · Chest',     route: '/tabs/tasks',                 childMode: false, action: 'chest',           settleMs: 5000 },
];

/** 1-based slide number (matches the labels) → slide, or null if out of range. */
export function slideByNumber(n: number): ScreenshotSlide | null {
  return SCREENSHOT_SLIDES[n - 1] ?? null;
}
